import { isEmpty, map, orderBy, sortBy } from 'lodash';

import { REQUEST_METHODS } from 'constants/api';
import { API_TAG_TYPES } from 'constants/apiTagTypes';
import { DATA_GRID_YES_NO_OPTIONS } from 'constants/components';
import { ALL_OPTIONS_VALUE, SORT_OPTIONS } from 'constants/common';
import { PROJECT_STATUSES } from 'constants/commonProjects';
import { frameworkValuesColors } from 'constants/feedbackFramework';
import { downloadFile } from 'helper/downloadFile';
import { baseApi } from 'store/baseApi';

const stripReportResponseNewlines = (report) => ({
  ...report,
  per_value_analysis: (report.per_value_analysis ?? []).map((analysis) => ({
    ...analysis,
    nominees_responses: analysis.nominees_responses.map((nomineeResponse) => ({
      ...nomineeResponse,
      response1: nomineeResponse.response1.replace(/\n/g, ''),
      response2: nomineeResponse.response2.replace(/\n/g, ''),
    })),
  })),
});

export const feedbackFrameworkApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    getFrameworkPreviewSurvey: build.query({
      query: ({ frameworkId }) => ({ url: `/feedback_framework/feedback-responses-preview/${frameworkId}` }),
      transformResponse: (baseQueryReturnValue) => ({
        ...baseQueryReturnValue,
        values: map(baseQueryReturnValue.values, (value) => ({
          ...value,
          qualities: sortBy(value.qualities, 'order'),
        })),
      }),
    }),
    rejectRecipientFeedbackResponse: build.mutation({
      query: ({ frameworkId, userId }) => ({
        url: `/feedback_framework/frameworks/${frameworkId}/users/${userId}/reject/`,
        method: REQUEST_METHODS.POST,
      }),
    }),
    getFrameworkRoles: build.query({
      query: () => ({ url: `/feedback_framework/roles/` }),
      transformResponse: (baseQueryReturnValue = []) => baseQueryReturnValue.map(({ id, name }) => ({ id, name })),
    }),
    getFeedbackFramework: build.query({
      query: ({ frameworkId }) => ({ url: `/feedback_framework/frameworks/${frameworkId}/` }),
      providesTags: (result, _error, { frameworkId }) => [
        { type: API_TAG_TYPES.FEEDBACK_FRAMEWORK, id: result?.id ?? frameworkId },
      ],
      transformResponse: (baseQueryReturnValue) => ({
        id: baseQueryReturnValue.pk ?? '',
        name: baseQueryReturnValue.name ?? '',
        roles: baseQueryReturnValue.roles ?? [],
        status: baseQueryReturnValue.status ?? PROJECT_STATUSES.DRAFT,
        organisationId: baseQueryReturnValue.organisation,
        organisationName: baseQueryReturnValue.organisation_name,
        question1: baseQueryReturnValue.question1 ?? '',
        question2: baseQueryReturnValue.question2 ?? '',
        recipientsWithReports: baseQueryReturnValue.recipients_with_reports ?? 0,
        values: orderBy(baseQueryReturnValue.values, ['order'], [SORT_OPTIONS.ASC]).map(({ order = 0, ...value }) => {
          const { backgroundColor: defaultBgColor, textColor: defaultTextColor } = frameworkValuesColors[order];
          const textColor = value.text_colour.toUpperCase();
          const bgColor = value.background_colour.toUpperCase();
          const isDefaultColors = textColor === defaultTextColor && bgColor === defaultBgColor;

          return {
            id: value.pk ?? '',
            name: value.name ?? '',
            textColor: isDefaultColors ? null : textColor,
            backgroundColor: isDefaultColors ? null : bgColor,
            qualities: orderBy(value.qualities, ['order'], [SORT_OPTIONS.ASC]).map((quality) => ({
              id: quality.id ?? '',
              name: quality.name ?? '',
            })),
          };
        }),
      }),
    }),
    createFeedbackFramework: build.mutation({
      query: (data) => ({
        url: `/feedback_framework/frameworks/`,
        body: data,
        method: REQUEST_METHODS.POST,
      }),
      invalidatesTags: (result) =>
        result
          ? [
              { type: API_TAG_TYPES.ORGANISATION_PROJECTS, id: 'LIST' },
              ...(result.status !== PROJECT_STATUSES.DRAFT ? [{ type: API_TAG_TYPES.ORGANISATION_PORTFOLIO }] : []),
            ]
          : [],
    }),
    updateFeedbackFramework: build.mutation({
      query: ({ frameworkId, data }) => ({
        url: `/feedback_framework/frameworks/${frameworkId}/`,
        body: data,
        method: REQUEST_METHODS.PATCH,
      }),
      invalidatesTags: (result) =>
        result
          ? [
              { type: API_TAG_TYPES.ORGANISATION_PROJECTS, id: 'LIST' },
              ...(result.status !== PROJECT_STATUSES.DRAFT
                ? [
                    { type: API_TAG_TYPES.ORGANISATION_PORTFOLIO },
                    { type: API_TAG_TYPES.PERSONAL_JOURNALS, id: 'LIST' },
                  ]
                : []),
              ...(result.status === PROJECT_STATUSES.DELETED
                ? [
                    { type: API_TAG_TYPES.ORGANISATION_ACTIVITY_LOGS, id: 'LIST' },
                    { type: API_TAG_TYPES.ORGANISATION_PORTFOLIO },
                  ]
                : [{ type: API_TAG_TYPES.FEEDBACK_FRAMEWORK, id: result.pk }]),
            ]
          : [],
    }),
    deleteFeedbackFramework: build.mutation({
      query: ({ frameworkId }) => ({
        url: `/feedback_framework/frameworks/${frameworkId}/`,
        method: REQUEST_METHODS.DELETE,
      }),
      invalidatesTags: (_result, error) => (!error ? [{ type: API_TAG_TYPES.ORGANISATION_PROJECTS, id: 'LIST' }] : []),
    }),
    duplicateFramework: build.mutation({
      query: ({ id, selectedOrganisation }) => ({
        url: '/feedback_framework/duplicate/',
        method: REQUEST_METHODS.POST,
        body: {
          framework_id: id,
          organisation_id: selectedOrganisation,
        },
      }),
      transformResponse: (baseQueryReturnValue) => baseQueryReturnValue.pk,
      invalidatesTags: (result) => (result ? [{ type: API_TAG_TYPES.ORGANISATION_PROJECTS, id: 'LIST' }] : []),
    }),
    getFrameworkUserRounds: build.query({
      query: ({ frameworkId }) => ({ url: `/feedback_framework/frameworks/${frameworkId}/users/user-rounds-info/` }),
      transformResponse: (baseQueryReturnValue) =>
        isEmpty(baseQueryReturnValue)
          ? []
          : baseQueryReturnValue
              .map(({ user, rounds: roundsInfo }) => {
                const rounds = roundsInfo
                  .filter((round) => !!round?.report_ready)
                  .toSorted((a, b) => b.round_number - a.round_number);

                return rounds.length
                  ? {
                      user,
                      lastRoundWithReport: rounds[0].id,
                      latestJournalId: rounds[0].personal_journal_id ?? null,
                      rounds: rounds.map((round) => ({
                        id: round.id,
                        roundNumber: round.round_number,
                        personalJournalId: round.personal_journal_id ?? null,
                      })),
                    }
                  : null;
              })
              .filter(Boolean),
    }),
    getTeamReport: build.query({
      query: ({ frameworkId, data }) => ({
        url: `/feedback_framework/frameworks/${frameworkId}/team-report/`,
        method: REQUEST_METHODS.POST,
        body: data,
      }),
    }),
    getFrameworkNomineesSettings: build.query({
      query: ({ frameworkId }) => ({
        url: '/feedback_framework/check-date-and-roles/',
        params: { framework_id: frameworkId },
      }),
      providesTags: (result, error, { frameworkId: id }) => [{ type: API_TAG_TYPES.NOMINEES_SETTINGS, id }],
      transformResponse: ({ roles, nominees, recipient }) => ({
        roles,
        recipient,
        nominees: !nominees?.length
          ? []
          : nominees.map((nominee) => ({
              first_name: nominee.first_name,
              last_name: nominee.last_name,
              email: nominee.email,
              role: nominee.role.id,
            })),
      }),
    }),
    sendFeedbackRequestToNominees: build.mutation({
      query: ({ frameworkId, data }) => ({
        url: `/feedback_framework/frameworks/${frameworkId}/send-feedback-request/`,
        method: REQUEST_METHODS.POST,
        body: data,
      }),
      invalidatesTags: (result, error, { frameworkId: id }) => [{ type: API_TAG_TYPES.NOMINEES_SETTINGS, id }],
    }),
    downloadFrameworkUsersCsv: build.mutation({
      queryFn: async ({ frameworkId, params, fileName }, api, extraOptions, baseQueryFn) => {
        const result = await baseQueryFn({
          url: `/feedback_framework/frameworks/${frameworkId}/users/download-csv/`,
          params,
          responseHandler: (response) => response.blob(),
        });

        downloadFile(result.data, fileName);

        return { data: null };
      },
    }),
    startNewRound: build.mutation({
      query: (data) => ({
        url: `/feedback_framework/rounds/new-round-request/`,
        method: REQUEST_METHODS.POST,
        body: data,
      }),
      invalidatesTags: [{ type: API_TAG_TYPES.FRAMEWORK_USERS, id: 'LIST' }],
    }),
    getNominees: build.query({
      query: (params) => ({
        url: '/feedback_framework/nominees/',
        params,
      }),
      providesTags: [{ type: API_TAG_TYPES.FRAMEWORK_NOMINEES }],
      transformResponse: (baseQueryReturnValue) => ({
        frameworkName: baseQueryReturnValue.framework_name,
        frameworkStatus: baseQueryReturnValue.framework_status,
        organisationName: baseQueryReturnValue.organisation_name,
        recipient: baseQueryReturnValue.recipient ?? {},
        rounds: (baseQueryReturnValue.rounds ?? []).map((round) => ({
          ...round,
          nominees: round.nominees.map((nominee) => ({
            ...nominee,
            request_accepted: !nominee.request_rejected ? DATA_GRID_YES_NO_OPTIONS.YES : DATA_GRID_YES_NO_OPTIONS.NO,
            feedback_submitted: nominee.feedback_submitted ? DATA_GRID_YES_NO_OPTIONS.YES : DATA_GRID_YES_NO_OPTIONS.NO,
          })),
        })),
      }),
    }),
    deleteNominee: build.mutation({
      query: ({ nomineeId }) => ({
        url: `/feedback_framework/nominees/${nomineeId}/`,
        method: REQUEST_METHODS.DELETE,
      }),
      invalidatesTags: [{ type: API_TAG_TYPES.FRAMEWORK_NOMINEES }],
    }),
    getFrameworkUsers: build.query({
      query: ({ frameworkId, params }) => ({
        url: `/feedback_framework/frameworks/${frameworkId}/users/`,
        params,
      }),
      transformResponse: (baseQueryReturnValue) =>
        baseQueryReturnValue
          .map((recipient) => ({
            ...recipient,
            id: recipient.user,
            personalJournalId: recipient.personal_journal_id ?? null,
            nominees_selected: recipient.nominees_selected ? DATA_GRID_YES_NO_OPTIONS.YES : DATA_GRID_YES_NO_OPTIONS.NO,
            self_reflection_submitted: recipient.self_reflection_submitted
              ? DATA_GRID_YES_NO_OPTIONS.YES
              : DATA_GRID_YES_NO_OPTIONS.NO,
            report_ready: recipient.report_ready ? DATA_GRID_YES_NO_OPTIONS.YES : DATA_GRID_YES_NO_OPTIONS.NO,
            created_at: new Date(recipient.created_at).getTime(),
          }))
          .toSorted((a, b) => b.created_at - a.created_at),
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ user: id }) => ({ type: API_TAG_TYPES.FRAMEWORK_USERS, id })),
              { type: API_TAG_TYPES.FRAMEWORK_USERS, id: 'LIST' },
            ]
          : [{ type: API_TAG_TYPES.FRAMEWORK_USERS, id: 'LIST' }],
    }),
    createFrameworkInvite: build.mutation({
      query: (data) => ({
        url: `/feedback_framework/frameworks/${data.framework_id}/users/`,
        method: REQUEST_METHODS.POST,
        body: data,
      }),
      invalidatesTags: [
        { type: API_TAG_TYPES.FRAMEWORK_USERS, id: 'LIST' },
        { type: API_TAG_TYPES.FEEDBACK_FRAMEWORK_INVITES, id: 'LIST' },
        { type: API_TAG_TYPES.ORGANISATION_USERS, id: 'LIST' },
        { type: API_TAG_TYPES.ORGANISATION_INVITES, id: 'LIST' },
        { type: API_TAG_TYPES.ORGANISATION_PROJECTS, id: 'LIST' },
        { type: API_TAG_TYPES.ORGANISATION_PORTFOLIO },
      ],
    }),
    updateRecipient: build.mutation({
      query: ({ id, data, feedbackId }) => ({
        url: `/feedback_framework/frameworks/${feedbackId}/users/${id}/`,
        method: REQUEST_METHODS.PATCH,
        body: data,
      }),
      invalidatesTags: ({ user: id }) => [{ type: API_TAG_TYPES.FRAMEWORK_USERS, id }],
    }),
    deactivateFrameworkRecipient: build.mutation({
      query: ({ frameworkId, data }) => ({
        url: `/feedback_framework/frameworks/${frameworkId}/users/deactivate/`,
        method: REQUEST_METHODS.DELETE,
        body: data,
      }),
      invalidatesTags: [
        { type: API_TAG_TYPES.FRAMEWORK_USERS, id: 'LIST' },
        { type: API_TAG_TYPES.ORGANISATION_USERS, id: 'LIST' },
        { type: API_TAG_TYPES.ORGANISATION_PORTFOLIO },
      ],
    }),
    cancelRecipientRound: build.mutation({
      query: (data) => ({
        url: '/feedback_framework/rounds/undo/',
        method: REQUEST_METHODS.POST,
        body: data,
      }),
      invalidatesTags: (result, error, arg) => [{ type: API_TAG_TYPES.FRAMEWORK_USERS, id: arg.user_id }],
    }),
    getFeedbackFrameworkSurvey: build.query({
      query: ({ nomineeId }) => ({ url: `/feedback_framework/nominees/${nomineeId}/feedback-responses/` }),
      extraOptions: { isPublic: true },
      transformResponse: (baseQueryReturnValue) => ({
        ...baseQueryReturnValue,
        values: map(baseQueryReturnValue.values, (value) => ({
          ...value,
          qualities: sortBy(value.qualities, 'order'),
        })),
      }),
    }),
    getNomineeOwnResponses: build.query({
      query: ({ nomineeId }) => ({
        url: `/feedback_framework/nominees/${nomineeId}/feedback-responses/get-own-responses/`,
      }),
      extraOptions: { isPublic: true },
    }),
    sendNomineeFeedbackResponse: build.mutation({
      query: ({ nomineeId, data }) => ({
        url: `/feedback_framework/nominees/${nomineeId}/feedback-responses/`,
        body: data,
        method: REQUEST_METHODS.POST,
      }),
      extraOptions: { isPublic: true },
      invalidatesTags: (_result, error, arg) =>
        !error && arg.isSelfReflection
          ? [
              { type: API_TAG_TYPES.ORGANISATION_PROJECTS, id: 'LIST' },
              { type: API_TAG_TYPES.FRAMEWORK_USERS, id: 'LIST' },
              { type: API_TAG_TYPES.ORGANISATION_PORTFOLIO },
            ]
          : [],
    }),
    getNomineeById: build.query({
      query: ({ nomineeId }) => ({ url: `/feedback_framework/nominees/${nomineeId}/` }),
      extraOptions: { isPublic: true },
    }),
    rejectNomineeFeedbackResponse: build.mutation({
      query: ({ nomineeId }) => ({
        url: `/feedback_framework/nominees/${nomineeId}/reject/`,
        method: REQUEST_METHODS.POST,
      }),
      extraOptions: { isPublic: true },
    }),
    getPersonalJournalsList: build.query({
      query: (params) => ({
        url: '/feedback_framework/personal-journals/',
        params: {
          ff_status: ALL_OPTIONS_VALUE,
          ...params,
        },
      }),
      providesTags: (result) => (result ? [{ type: API_TAG_TYPES.PERSONAL_JOURNALS, id: 'LIST' }] : []),
      transformResponse: (baseQueryReturnValue) => ({
        ...baseQueryReturnValue,
        results: baseQueryReturnValue.results.map((journal) => ({
          ...journal,
          framework_name: journal.feedback_framework_name,
        })),
      }),
    }),
    getPersonalJournal: build.query({
      query: ({ journalId }) => ({ url: `/feedback_framework/personal-journals/${journalId}/` }),
      providesTags: (result) => (result?.id ? [{ type: API_TAG_TYPES.PERSONAL_JOURNAL, id: result.id }] : []),
    }),
    getPersonalJournalReportData: build.query({
      query: ({ journalId }) => ({ url: `/feedback_framework/personal-journals/${journalId}/report-data/` }),
      providesTags: (result) => (result?.id ? [{ type: API_TAG_TYPES.PERSONAL_JOURNAL, id: result.id }] : []),
      transformResponse: (baseQueryReturnValue) => ({
        ...baseQueryReturnValue,
        report: stripReportResponseNewlines(baseQueryReturnValue.report),
      }),
    }),
    getFrameworkPersonalJournalId: build.query({
      query: ({ frameworkId, roundNumber }) => ({
        url: `/feedback_framework/frameworks/${frameworkId}/personal-journal/`,
        params: roundNumber ? { round_number: roundNumber } : {},
      }),
    }),
    updatePersonalJournalTakeouts: build.mutation({
      query: ({ journalId, data }) => ({
        url: `/feedback_framework/personal-journals/${journalId}/`,
        method: REQUEST_METHODS.PATCH,
        body: data,
      }),
      invalidatesTags: (result, error) => (error ? [] : [{ type: API_TAG_TYPES.PERSONAL_JOURNALS, id: 'LIST' }]),
    }),
    createPersonalAction: build.mutation({
      query: (data) => ({
        url: '/feedback_framework/personal-actions/',
        method: REQUEST_METHODS.POST,
        body: data,
      }),
      invalidatesTags: (result, error, { journal }) =>
        error
          ? []
          : [
              { type: API_TAG_TYPES.PERSONAL_JOURNAL, id: journal },
              { type: API_TAG_TYPES.PERSONAL_JOURNALS, id: 'LIST' },
            ],
    }),
    updatePersonalAction: build.mutation({
      query: ({ actionId, data }) => ({
        url: `/feedback_framework/personal-actions/${actionId}/`,
        method: REQUEST_METHODS.PATCH,
        body: data,
      }),
      onQueryStarted: async ({ actionId, journalId, data }, { dispatch, queryFulfilled }) => {
        const patchCachedAction = (updates) =>
          dispatch(
            feedbackFrameworkApi.util.updateQueryData('getPersonalJournal', { journalId }, (draft) => {
              const cachedAction = draft.actions?.find((action) => action.id === actionId);

              if (cachedAction) {
                Object.assign(cachedAction, updates);
              }
            }),
          );

        const optimisticPatch = patchCachedAction(data);

        try {
          const { data: updatedAction } = await queryFulfilled;

          patchCachedAction(updatedAction);
        } catch {
          optimisticPatch.undo();
        }
      },
      invalidatesTags: (result, error, { journalId }) =>
        error
          ? []
          : [
              { type: API_TAG_TYPES.PERSONAL_JOURNAL, id: journalId },
              { type: API_TAG_TYPES.PERSONAL_JOURNALS, id: 'LIST' },
            ],
    }),
    deletePersonalAction: build.mutation({
      query: ({ actionId }) => ({
        url: `/feedback_framework/personal-actions/${actionId}/`,
        method: REQUEST_METHODS.DELETE,
      }),
      invalidatesTags: (result, error, { journalId }) =>
        error
          ? []
          : [
              { type: API_TAG_TYPES.PERSONAL_JOURNAL, id: journalId },
              { type: API_TAG_TYPES.PERSONAL_JOURNALS, id: 'LIST' },
            ],
    }),
  }),
});

export const {
  useGetFrameworkPreviewSurveyQuery,
  useRejectRecipientFeedbackResponseMutation,
  useGetFrameworkRolesQuery,
  useGetFeedbackFrameworkQuery,
  useCreateFeedbackFrameworkMutation,
  useUpdateFeedbackFrameworkMutation,
  useDeleteFeedbackFrameworkMutation,
  useDuplicateFrameworkMutation,
  useGetFrameworkUserRoundsQuery,
  useGetTeamReportQuery,
  useGetFrameworkNomineesSettingsQuery,
  useSendFeedbackRequestToNomineesMutation,
  useDownloadFrameworkUsersCsvMutation,
  useStartNewRoundMutation,
  useGetNomineesQuery,
  useDeleteNomineeMutation,
  useGetFrameworkUsersQuery,
  useCreateFrameworkInviteMutation,
  useUpdateRecipientMutation,
  useDeactivateFrameworkRecipientMutation,
  useCancelRecipientRoundMutation,
  useGetFeedbackFrameworkSurveyQuery,
  useGetNomineeOwnResponsesQuery,
  useSendNomineeFeedbackResponseMutation,
  useGetNomineeByIdQuery,
  useRejectNomineeFeedbackResponseMutation,
  useGetPersonalJournalsListQuery,
  useGetPersonalJournalQuery,
  useLazyGetPersonalJournalQuery,
  useGetPersonalJournalReportDataQuery,
  useLazyGetPersonalJournalReportDataQuery,
  useGetFrameworkPersonalJournalIdQuery,
  useLazyGetFrameworkPersonalJournalIdQuery,
  useUpdatePersonalJournalTakeoutsMutation,
  useCreatePersonalActionMutation,
  useUpdatePersonalActionMutation,
  useDeletePersonalActionMutation,
} = feedbackFrameworkApi;
